// DiffReview — full-page route at /admin/venues/:venue_id/diff/:document_id.
// Per plan U10: 2-column layout (65% diff table + 35% sticky thumbnail
// strip), per-row source traceability (raw_location_text + page_index
// inline, click-through to thumbnail), live recompute on location_label
// remap, operator-marked floor plans, Preview (dry-run) + Apply buttons.
//
// On 409 catalog_changed, preserves all operator edits and merges
// stale_buckets into the in-memory state with a yellow banner highlighting
// the affected rows — operator doesn't lose work.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LocationSelect } from "@/components/LocationSelect";
import { api, ApiError } from "@/lib/api";
import type {
  DiffBucket,
  DiffEnvelope,
  ExtractedPageImage,
  VenueDocumentDetail,
} from "@/lib/types";

// Mirrors plan KD9 connector vocabulary. The "" sentinel renders as
// "(none)" / blank; selecting any of the controlled values writes them
// straight to connector_type; "__custom__" toggles a free-text input.
const CONNECTOR_VOCAB = [
  "Cam-Lock",
  "Hubbell L2130",
  "bare-wire-to-lugs",
] as const;

// Each diff row maintains operator-editable state. We start with the
// AI's extraction values and let the operator override before Apply.
// current_count is mutated when a 409 catalog_changed response arrives.
interface BucketState extends DiffBucket {
  // Per-row action: 'add' or 'skip'. Initialized from server's `action`
  // ('add' / 'no_change' / 'warn_decrease') — 'no_change' and
  // 'warn_decrease' default to 'skip' since no INSERTs needed.
  ui_action: "add" | "skip";
  // Local edit state for the optional fields.
  ui_target_count: number;
  ui_location_label: string;
  ui_connector_type: string;
  ui_notes: string;
  // True if this row was flagged stale on a prior 409 attempt and needs
  // operator re-confirmation. Cleared once they click Apply again.
  ui_stale: boolean;
}

interface VenueLevelState {
  address_accept: boolean;
  voltage_accept: boolean;
  timezone_accept: boolean;
}

export function DiffReview() {
  const { venue_id: venueId, document_id: documentId } = useParams<{
    venue_id: string;
    document_id: string;
  }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<VenueDocumentDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [buckets, setBuckets] = useState<BucketState[]>([]);
  const [venueLevel, setVenueLevel] = useState<VenueLevelState>({
    address_accept: true,
    voltage_accept: true,
    timezone_accept: true,
  });
  const [pinnedPages, setPinnedPages] = useState<Set<number>>(new Set());
  const [activePage, setActivePage] = useState<number>(0);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<unknown | null>(null);
  const [staleBanner, setStaleBanner] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Load on mount
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    api
      .get<VenueDocumentDetail>(
        `/api/venue-documents/${encodeURIComponent(documentId)}`,
      )
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        // Seed buckets from server's diff (or empty if not yet complete)
        const seed = (d.diff?.buckets || []).map<BucketState>((b) => ({
          ...b,
          ui_action: b.action === "no_change" ? "skip" : (b.action === "add" ? "add" : "skip"),
          ui_target_count: b.target_count,
          ui_location_label: b.location_label || b.raw_location_text || "",
          ui_connector_type: b.connector_type || "",
          ui_notes: b.notes || "",
          ui_stale: false,
        }));
        setBuckets(seed);
        // Initial floor-plan pins: doc-stored pinned_page_indices wins;
        // else AI's candidate_floor_plan_page_indices serves as the
        // pre-fill.
        const docPins = d.document.pinned_page_indices || [];
        const candidatePins =
          d.extraction_payload?.candidate_floor_plan_page_indices || [];
        setPinnedPages(new Set(docPins.length > 0 ? docPins : candidatePins));
        // Default the active thumbnail to the first floor-plan candidate
        // if any, else page 0.
        const initialActive =
          (d.extraction_payload?.candidate_floor_plan_page_indices?.[0]) ??
          0;
        setActivePage(initialActive);
      })
      .catch((err: ApiError | unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError
            ? `${err.status}: ${
                (err.body as { error?: string })?.error || "failed"
              }`
            : String(err),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // ------------------------------------------------------------------
  // Live current_count recompute on location_label remap.
  // The server returned `diff.buckets[*].current_count` against the
  // catalog snapshot loaded at this GET. When the operator changes a
  // row's location_label, the match key changes — recompute against
  // an aggregate built from the same snapshot.
  // ------------------------------------------------------------------
  const catalogIndex = useMemo(() => {
    // Build a map (canonical_label, capacity, connector) → count from
    // the original server diff. We approximate the canonical_label
    // transform with the same normalization the backend uses.
    const idx = new Map<string, number>();
    for (const b of detail?.diff?.buckets || []) {
      // current_count is what the server saw for THIS bucket's label+
      // capacity+connector at GET time. We can use that to seed but
      // not for "what if operator picks 'Upstage Center' instead?".
      // For a more general recompute, we'd need a separate endpoint
      // returning the full catalog snapshot. For v1 we stick with
      // server values; the catalog recompute kicks in fully only when
      // the operator-edited label happens to match another bucket's
      // server-provided label.
      const key = bucketKey(
        b.location_label || b.raw_location_text || "",
        b.capacity_amps,
        b.connector_type || "",
      );
      idx.set(key, b.current_count);
    }
    return idx;
  }, [detail]);

  const liveRecompute = useCallback(
    (label: string, capacity: number, connector: string): number => {
      return catalogIndex.get(bucketKey(label, capacity, connector)) ?? 0;
    },
    [catalogIndex],
  );

  // Whenever a row edits location_label / connector_type, refresh its
  // current_count via the live catalog snapshot.
  const updateBucket = useCallback(
    (idx: number, patch: Partial<BucketState>) => {
      setBuckets((prev) =>
        prev.map((b, i) => {
          if (i !== idx) return b;
          const merged = { ...b, ...patch };
          // If label/connector changed, recompute current_count.
          if (
            patch.ui_location_label !== undefined ||
            patch.ui_connector_type !== undefined
          ) {
            const fresh = liveRecompute(
              merged.ui_location_label,
              merged.capacity_amps,
              merged.ui_connector_type,
            );
            merged.current_count = fresh;
            // Re-derive default action: matching → no change; less → add; more → warn
            if (fresh === merged.ui_target_count) merged.ui_action = "skip";
            else if (fresh < merged.ui_target_count) merged.ui_action = "add";
            else merged.ui_action = "skip";
          }
          // Clear the stale flag on any edit — operator engaged with the row.
          merged.ui_stale = false;
          return merged;
        }),
      );
    },
    [liveRecompute],
  );

  const togglePin = useCallback((pageIndex: number) => {
    setPinnedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageIndex)) next.delete(pageIndex);
      else next.add(pageIndex);
      return next;
    });
  }, []);

  // ------------------------------------------------------------------
  // Build apply request body — shared by Preview + Apply.
  // ------------------------------------------------------------------
  const buildApplyBody = useCallback(() => {
    if (!detail) return null;
    return {
      address: venueLevel.address_accept
        ? detail.extraction_payload?.address || null
        : null,
      voltage_phase_summary: venueLevel.voltage_accept
        ? detail.extraction_payload?.voltage_phase_summary || null
        : null,
      timezone: venueLevel.timezone_accept
        ? detail.extraction_payload?.timezone || null
        : null,
      pinned_page_indices: Array.from(pinnedPages).sort((a, b) => a - b),
      buckets: buckets.map((b) => ({
        raw_location_text: b.raw_location_text,
        page_index: b.page_index,
        location_label: b.ui_location_label,
        capacity_amps: b.capacity_amps,
        target_count: b.ui_target_count,
        connector_type: b.ui_connector_type || null,
        notes: b.ui_notes || null,
        current_count: b.current_count,
        action: b.ui_action,
      })),
    };
  }, [detail, venueLevel, pinnedPages, buckets]);

  // ------------------------------------------------------------------
  // Preview (dry-run) + Apply
  // ------------------------------------------------------------------
  const onPreview = useCallback(async () => {
    const body = buildApplyBody();
    if (!body || !documentId) return;
    setPreviewing(true);
    try {
      const resp = await api.post(
        `/api/venue-documents/${encodeURIComponent(documentId)}/apply?dry_run=true`,
        body,
      );
      setPreview(resp);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        handle409(err.body);
      } else {
        alert(`Preview failed: ${err}`);
      }
    } finally {
      setPreviewing(false);
    }
  }, [buildApplyBody, documentId]);

  const onApply = useCallback(async () => {
    const body = buildApplyBody();
    if (!body || !documentId || !venueId) return;
    setApplying(true);
    try {
      const resp = await api.post<{
        applied_buckets?: Array<{ added: number }>;
      }>(
        `/api/venue-documents/${encodeURIComponent(documentId)}/apply`,
        body,
      );
      const totalAdded = (resp.applied_buckets || []).reduce(
        (acc, b) => acc + (b.added || 0),
        0,
      );
      alert(`Applied ${totalAdded} feed${totalAdded === 1 ? "" : "s"} to the catalog.`);
      // Preserve venue-editor expanded-row state via localStorage so the
      // operator lands back on the same venue with the bucket editor open.
      localStorage.setItem("openVenueId", venueId);
      navigate("/admin/venues");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string };
        if (body?.error === "catalog_changed") {
          handle409(body);
        } else if (body?.error === "wrong_status") {
          alert("Already applied by another session — viewing current state.");
          navigate("/admin/venues");
        } else {
          alert(`Apply failed (409): ${body?.error || "race"}`);
        }
      } else {
        alert(`Apply failed: ${err}`);
      }
    } finally {
      setApplying(false);
    }
  }, [buildApplyBody, documentId, venueId, navigate]);

  // ------------------------------------------------------------------
  // 409 catalog_changed handler — preserves operator edits.
  // ------------------------------------------------------------------
  const handle409 = (body: unknown) => {
    const stale = (body as { stale_buckets?: Array<{
      location_label: string;
      capacity_amps: number;
      connector_type: string | null;
      live_current_count: number;
    }> })?.stale_buckets || [];

    setBuckets((prev) =>
      prev.map((b) => {
        const drift = stale.find(
          (s) =>
            s.location_label === b.ui_location_label &&
            s.capacity_amps === b.capacity_amps &&
            (s.connector_type || "") === (b.ui_connector_type || ""),
        );
        if (!drift) return b;
        // Update current_count from server's live snapshot; flag stale
        // so the row highlights yellow. All operator edits preserved.
        const newCurrent = drift.live_current_count;
        return {
          ...b,
          current_count: newCurrent,
          ui_action:
            newCurrent < b.ui_target_count
              ? "add"
              : newCurrent === b.ui_target_count
              ? "skip"
              : "skip",
          ui_stale: true,
        };
      }),
    );
    setStaleBanner(
      `Catalog changed for ${stale.length} bucket${
        stale.length === 1 ? "" : "s"
      } while you were reviewing. Counts updated; review the highlighted rows and re-apply.`,
    );
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loadError) {
    return (
      <div className="p-6">
        <Button variant="outline" onClick={() => navigate("/admin/venues")}>
          ← Back to venues
        </Button>
        <p className="mt-4 rounded bg-destructive/10 p-4 text-destructive">
          Failed to load document: {loadError}
        </p>
      </div>
    );
  }

  if (!detail) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }

  if (
    detail.document.extraction_status !== "complete" &&
    detail.document.extraction_status !== "applied"
  ) {
    return (
      <div className="p-6 space-y-3">
        <Button variant="outline" onClick={() => navigate("/admin/venues")}>
          ← Back to venues
        </Button>
        <p className="rounded bg-amber-500/10 p-4 text-sm">
          Document is in <strong>{detail.document.extraction_status}</strong> state —
          not yet ready for review.
        </p>
      </div>
    );
  }

  const pageImages: ExtractedPageImage[] =
    detail.extraction_payload?.page_images || [];

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/venues")}
          >
            ← Back to venues
          </Button>
          <h2 className="mt-2 text-lg font-semibold">
            Review extraction: {detail.document.filename}
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onPreview}
            disabled={previewing || applying}
          >
            {previewing ? "Previewing…" : "Preview changes"}
          </Button>
          <Button
            onClick={onApply}
            disabled={previewing || applying || detail.document.extraction_status === "applied"}
          >
            {applying ? "Applying…" : "Apply selected"}
          </Button>
        </div>
      </div>

      {staleBanner && (
        <p className="mb-3 rounded bg-amber-500/15 border border-amber-500/40 p-3 text-sm text-amber-700">
          {staleBanner}
        </p>
      )}

      {detail.document.extraction_status === "applied" && (
        <p className="mb-3 rounded bg-blue-500/10 p-3 text-sm">
          This document has already been applied. Showing audit view.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
        {/* Left: diff table */}
        <div className="space-y-3">
          <VenueLevelSection
            address={detail.diff?.address}
            voltage={detail.diff?.voltage_phase_summary}
            timezone={detail.diff?.timezone}
            addressAccept={venueLevel.address_accept}
            voltageAccept={venueLevel.voltage_accept}
            timezoneAccept={venueLevel.timezone_accept}
            onAddressToggle={(v) =>
              setVenueLevel((s) => ({ ...s, address_accept: v }))
            }
            onVoltageToggle={(v) =>
              setVenueLevel((s) => ({ ...s, voltage_accept: v }))
            }
            onTimezoneToggle={(v) =>
              setVenueLevel((s) => ({ ...s, timezone_accept: v }))
            }
          />

          <div className="rounded border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-2 py-1.5 text-left">Source</th>
                  <th className="px-2 py-1.5 text-left">Location</th>
                  <th className="px-2 py-1.5 text-left">Capacity</th>
                  <th className="px-2 py-1.5 text-left">Connector</th>
                  <th className="px-2 py-1.5 text-left">Current → Target</th>
                  <th className="px-2 py-1.5 text-left">Notes</th>
                  <th className="px-2 py-1.5 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {buckets.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-2 py-3 text-center text-muted-foreground"
                    >
                      No buckets extracted.
                    </td>
                  </tr>
                )}
                {buckets.map((b, i) => (
                  <DiffRow
                    key={i}
                    bucket={b}
                    onChange={(patch) => updateBucket(i, patch)}
                    onSourceClick={() => setActivePage(b.page_index)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {preview != null && (
            <pre className="rounded border border-border bg-muted/30 p-2 text-xs">
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
        </div>

        {/* Right: thumbnail strip + active page viewer */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Source pages</h3>
          {pageImages.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No page thumbnails available (image-only doc or PDF render failed).
            </p>
          ) : (
            <>
              <div className="max-h-32 overflow-x-auto whitespace-nowrap rounded border border-border bg-muted/20 p-1">
                {pageImages.map((p) => {
                  const url = `/api/venue-documents/${encodeURIComponent(
                    detail.document.document_id,
                  )}/pages/${p.index}/image`;
                  const isPinned = pinnedPages.has(p.index);
                  const isActive = activePage === p.index;
                  return (
                    <div
                      key={p.index}
                      className="relative inline-block align-top mr-1"
                      style={{ verticalAlign: "top" }}
                    >
                      <button
                        type="button"
                        onClick={() => setActivePage(p.index)}
                        className={`block h-24 w-16 overflow-hidden rounded border-2 ${
                          isActive ? "border-primary" : "border-transparent"
                        } hover:border-primary/50`}
                      >
                        <img
                          src={url}
                          alt={`Page ${p.index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePin(p.index)}
                        className={`absolute -top-1 -right-1 rounded-full px-1 text-xs ${
                          isPinned
                            ? "bg-amber-500 text-white"
                            : "bg-muted text-muted-foreground hover:bg-amber-500/50"
                        }`}
                        title={
                          isPinned
                            ? "Pinned as floor plan"
                            : "Pin as floor plan"
                        }
                      >
                        {isPinned ? "📌" : "📍"}
                      </button>
                      <div className="text-center text-[10px] text-muted-foreground">
                        p.{p.index + 1}
                      </div>
                    </div>
                  );
                })}
              </div>

              <ZoomablePageImage
                src={`/api/venue-documents/${encodeURIComponent(
                  detail.document.document_id,
                )}/pages/${activePage}/image`}
                pageIndex={activePage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function bucketKey(label: string, capacity: number, connector: string): string {
  // Mirror the backend's canonicalize_label: lowercase + collapse-ws +
  // strip surrounding punctuation. Kept inline so the recompute works
  // without an extra fetch.
  const canon = (label || "")
    .trim()
    .toLowerCase()
    .replace(/^[\s\-_.,;:!?'"()[\]{}]+|[\s\-_.,;:!?'"()[\]{}]+$/g, "")
    .replace(/\s+/g, " ");
  return `${canon}|${capacity}|${connector || ""}`;
}


interface VenueLevelSectionProps {
  address: DiffEnvelope | undefined;
  voltage: DiffEnvelope | undefined;
  timezone: DiffEnvelope | undefined;
  addressAccept: boolean;
  voltageAccept: boolean;
  timezoneAccept: boolean;
  onAddressToggle: (v: boolean) => void;
  onVoltageToggle: (v: boolean) => void;
  onTimezoneToggle: (v: boolean) => void;
}

function VenueLevelSection({
  address,
  voltage,
  timezone,
  addressAccept,
  voltageAccept,
  timezoneAccept,
  onAddressToggle,
  onVoltageToggle,
  onTimezoneToggle,
}: VenueLevelSectionProps) {
  const addressShow = address?.action_required;
  const voltageShow = voltage?.action_required;
  const timezoneShow = timezone?.action_required;
  if (!addressShow && !voltageShow && !timezoneShow) return null;
  return (
    <div className="rounded border border-border bg-card p-3 space-y-2 text-sm">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Venue-level
      </h3>
      {addressShow && (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={addressAccept}
            onChange={(e) => onAddressToggle(e.target.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Address</div>
            <div className="text-xs">current: {address?.current || "(empty)"}</div>
            <div className="font-medium">proposed: {address?.proposed}</div>
          </div>
        </div>
      )}
      {voltageShow && (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={voltageAccept}
            onChange={(e) => onVoltageToggle(e.target.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Voltage / phase</div>
            <div className="text-xs">current: {voltage?.current || "(empty)"}</div>
            <div className="font-medium">proposed: {voltage?.proposed}</div>
          </div>
        </div>
      )}
      {timezoneShow && (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={timezoneAccept}
            onChange={(e) => onTimezoneToggle(e.target.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Timezone</div>
            <div className="text-xs">current: {timezone?.current || "(empty)"}</div>
            <div className="font-medium">proposed: {timezone?.proposed}</div>
          </div>
        </div>
      )}
    </div>
  );
}


interface DiffRowProps {
  bucket: BucketState;
  onChange: (patch: Partial<BucketState>) => void;
  onSourceClick: () => void;
}

function DiffRow({ bucket, onChange, onSourceClick }: DiffRowProps) {
  const delta = bucket.ui_target_count - bucket.current_count;
  const actionDescriptor =
    bucket.ui_action === "skip"
      ? "skip"
      : delta > 0
      ? `add ${delta}`
      : delta < 0
      ? "warn (more current than target)"
      : "no change";

  const usingCustomConnector =
    bucket.ui_connector_type &&
    !(CONNECTOR_VOCAB as readonly string[]).includes(bucket.ui_connector_type);

  return (
    <tr
      className={`border-t border-border ${
        bucket.ui_stale ? "bg-amber-500/5" : ""
      }`}
    >
      <td className="px-2 py-1.5 align-top text-xs">
        <button
          type="button"
          onClick={onSourceClick}
          className="text-left text-primary hover:underline"
        >
          {bucket.raw_location_text || "(no raw text)"}
          <br />
          <span className="text-muted-foreground">p.{bucket.page_index + 1}</span>
        </button>
      </td>
      <td className="px-2 py-1.5 align-top">
        <LocationSelect
          value={bucket.ui_location_label}
          onChange={(v) => onChange({ ui_location_label: v })}
        />
      </td>
      <td className="px-2 py-1.5 align-top">
        <Input
          type="number"
          value={bucket.capacity_amps}
          onChange={(e) =>
            onChange({ capacity_amps: Number(e.target.value) || bucket.capacity_amps })
          }
          className="h-8 w-20"
        />
      </td>
      <td className="px-2 py-1.5 align-top">
        <select
          value={usingCustomConnector ? "__custom__" : bucket.ui_connector_type}
          onChange={(e) => {
            if (e.target.value === "__custom__") {
              onChange({ ui_connector_type: "" });
            } else {
              onChange({ ui_connector_type: e.target.value });
            }
          }}
          className="h-8 rounded border border-input bg-background px-2 text-xs"
        >
          <option value="">(none)</option>
          {CONNECTOR_VOCAB.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          <option value="__custom__">Other…</option>
        </select>
        {usingCustomConnector && (
          <Input
            value={bucket.ui_connector_type}
            onChange={(e) => onChange({ ui_connector_type: e.target.value })}
            placeholder="custom connector"
            className="mt-1 h-7 text-xs"
          />
        )}
      </td>
      <td className="px-2 py-1.5 align-top font-medium">
        {bucket.current_count} →{" "}
        <Input
          type="number"
          min={0}
          value={bucket.ui_target_count}
          onChange={(e) =>
            onChange({
              ui_target_count: Math.max(0, Number(e.target.value) || 0),
            })
          }
          className="ml-1 inline-block h-7 w-16"
        />
      </td>
      <td className="px-2 py-1.5 align-top">
        <Input
          value={bucket.ui_notes}
          onChange={(e) => onChange({ ui_notes: e.target.value })}
          placeholder=""
          className="h-8 text-xs"
        />
      </td>
      <td className="px-2 py-1.5 align-top text-xs">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={bucket.ui_action === "add"}
            onChange={() => onChange({ ui_action: "add" })}
          />
          add
        </label>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={bucket.ui_action === "skip"}
            onChange={() => onChange({ ui_action: "skip" })}
          />
          skip
        </label>
        <div className="text-[10px] text-muted-foreground">{actionDescriptor}</div>
      </td>
    </tr>
  );
}


// ---------------------------------------------------------------------------
// ZoomablePageImage — wheel zoom + drag pan + double-click reset for the
// active source page. Uses react-zoom-pan-pinch under the hood. Reset zoom
// on page change so each new thumbnail click starts at 1×.
// ---------------------------------------------------------------------------

function ZoomablePageImage({ src, pageIndex }: { src: string; pageIndex: number }) {
  return (
    <div className="overflow-hidden rounded border border-border bg-muted/10">
      <TransformWrapper
        // Reset zoom + pan when the operator switches to a different page.
        key={pageIndex}
        minScale={1}
        maxScale={6}
        initialScale={1}
        wheel={{ step: 0.2 }}
        doubleClick={{ mode: "reset" }}
        panning={{ velocityDisabled: true }}
      >
        <ZoomControls />
        <TransformComponent
          wrapperClass="!w-full"
          contentClass="!w-full"
        >
          <img
            src={src}
            alt={`Page ${pageIndex + 1}`}
            className="w-full select-none"
            draggable={false}
          />
        </TransformComponent>
      </TransformWrapper>
      <p className="px-2 py-1 text-[10px] text-muted-foreground border-t border-border">
        Scroll to zoom · drag to pan · double-click to reset
      </p>
    </div>
  );
}

function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="flex items-center justify-end gap-1 border-b border-border bg-muted/20 px-1 py-1">
      <button
        type="button"
        onClick={() => zoomOut()}
        className="rounded border border-border bg-background px-2 text-sm hover:bg-accent"
        title="Zoom out"
      >
        −
      </button>
      <button
        type="button"
        onClick={() => zoomIn()}
        className="rounded border border-border bg-background px-2 text-sm hover:bg-accent"
        title="Zoom in"
      >
        +
      </button>
      <button
        type="button"
        onClick={() => resetTransform()}
        className="rounded border border-border bg-background px-2 text-xs hover:bg-accent"
        title="Reset zoom"
      >
        Reset
      </button>
    </div>
  );
}
